import { Box, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { showError, showSuccess } from 'src/toasts/toasts';
import { expenditureColumns, incomeColumns, lifeEventColumns } from './cashflowColumns';
import { toEditableExpenditure, toEditableIncome, toEditableLifeEvent } from './cashflowMappers';
import CashflowContentDeprecated from './CashflowContentDeprecated';
import CashflowSection from './components/CashflowSection';
import CashflowTable from './components/CashflowTable';
import {
  ExpenditureSectionTotal,
  IncomeSectionTotal,
  LifeEventSectionTotal,
} from './components/cashflowCells';
import { ExpenditureModal } from './form/ExpenditureModal';
import { IncomeModal } from './form/IncomeModal';
import { LifeEventModal } from './form/LifeEventModal';
import useExpenditures, { EditableExpenditure, Expenditure } from './hooks/useExpenditures';
import useIncomes, { EditableIncome, Income } from './hooks/useIncomes';
import useLifeEvents, { LifeEvent } from './hooks/useLifeEvents';
import { CashflowModelsSection } from './modelling/CashflowModelsSection';
import { Period } from './utils';
import { useCurrency } from 'src/common/hooks/useCurrency';

export default function CashflowContent() {
  const { flagEnabledFamilyCashflowsRevamp, flagEnabledCashflowModelling } = useFlags();

  return (
    <>
      {flagEnabledFamilyCashflowsRevamp ? (
        <CashflowContentInternal />
      ) : (
        <CashflowContentDeprecated />
      )}
      {flagEnabledCashflowModelling && (
        <Box mt="xl" pb="xl">
          <CashflowModelsSection />
        </Box>
      )}
    </>
  );
}

function CashflowContentInternal() {
  const currency = useCurrency();
  const { isLoading: loadingIncomes, incomesData, upsertIncomes, deleteIncomes } = useIncomes();
  const {
    isLoading: loadingExpenditures,
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
  } = useExpenditures();
  const { lifeEventsData, upsertLifeEvents, deleteLifeEvents } = useLifeEvents();

  const [incomePeriod, setIncomePeriod] = useState<Period>('monthly');
  const [expenditurePeriod, setExpenditurePeriod] = useState<Period>('monthly');

  const [incomeModalOpened, { open: openIncomeModal, close: closeIncomeModal }] =
    useDisclosure(false);
  const [selectedIncome, setSelectedIncome] = useState<EditableIncome | undefined>();

  const [expenditureModalOpened, { open: openExpenditureModal, close: closeExpenditureModal }] =
    useDisclosure(false);
  const [selectedExpenditure, setSelectedExpenditure] = useState<EditableExpenditure | undefined>();

  const [lifeEventModalOpened, { open: openLifeEventModal, close: closeLifeEventModal }] =
    useDisclosure(false);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState<LifeEvent | undefined>();

  if (loadingIncomes || loadingExpenditures) {
    return <PageLoader absolute={false} />;
  }

  const incomes = incomesData ?? [];
  const expenditures = expendituresData ?? [];
  const lifeEvents = lifeEventsData ?? [];

  const handleSaveIncome = async (income: EditableIncome) => {
    try {
      await upsertIncomes([income]);
      showSuccess('Income saved successfully');
      closeIncomeModal();
      setSelectedIncome(undefined);
    } catch {
      showError('Failed to save income');
    }
  };

  const handleSaveExpenditure = async (
    expenditure: Omit<EditableExpenditure, 'id' | 'clientId'>,
  ) => {
    const item = { ...expenditure, id: selectedExpenditure?.id };
    try {
      await upsertExpenditures([item]);
      showSuccess('Expenditure saved successfully');
      closeExpenditureModal();
      setSelectedExpenditure(undefined);
    } catch {
      showError('Failed to save expenditure');
    }
  };

  const handleSaveLifeEvent = async (lifeEvent: Omit<LifeEvent, 'id' | 'clientId'>) => {
    const item = { ...lifeEvent, id: selectedLifeEvent?.id };
    try {
      await upsertLifeEvents([item]);
      showSuccess('Life event saved successfully');
      closeLifeEventModal();
      setSelectedLifeEvent(undefined);
    } catch {
      showError('Failed to save life event');
    }
  };

  return (
    <Stack gap="md">
      <CashflowSection
        value="incomes"
        title="Income"
        count={incomes.length}
        period={incomePeriod}
        onPeriodChange={setIncomePeriod}
        showPeriodToggle
        onAdd={() => {
          setSelectedIncome(undefined);
          openIncomeModal();
        }}
        totalNode={
          <IncomeSectionTotal incomes={incomes} period={incomePeriod} currency={currency} />
        }
      >
        <CashflowTable<Income>
          items={incomes}
          columns={incomeColumns(incomePeriod, currency)}
          onEdit={income => {
            setSelectedIncome(toEditableIncome(income));
            openIncomeModal();
          }}
          onDelete={income => income.id && deleteIncomes([income.id])}
        />
      </CashflowSection>

      <CashflowSection
        value="expenditures"
        title="Expenditure"
        count={expenditures.length}
        period={expenditurePeriod}
        onPeriodChange={setExpenditurePeriod}
        showPeriodToggle
        onAdd={() => {
          setSelectedExpenditure(undefined);
          openExpenditureModal();
        }}
        totalNode={
          <ExpenditureSectionTotal
            expenditures={expenditures}
            period={expenditurePeriod}
            currency={currency}
          />
        }
      >
        <CashflowTable<Expenditure>
          items={expenditures}
          columns={expenditureColumns(expenditurePeriod, currency)}
          onEdit={expenditure => {
            setSelectedExpenditure(toEditableExpenditure(expenditure));
            openExpenditureModal();
          }}
          onDelete={expenditure => expenditure.id && deleteExpenditures([expenditure.id])}
        />
      </CashflowSection>

      <CashflowSection
        value="lifeEvents"
        title="Future life events"
        count={lifeEvents.length}
        onAdd={() => {
          setSelectedLifeEvent(undefined);
          openLifeEventModal();
        }}
        totalNode={<LifeEventSectionTotal lifeEvents={lifeEvents} currency={currency} />}
      >
        <CashflowTable<LifeEvent>
          items={lifeEvents}
          columns={lifeEventColumns(currency)}
          onEdit={lifeEvent => {
            setSelectedLifeEvent(toEditableLifeEvent(lifeEvent));
            openLifeEventModal();
          }}
          onDelete={lifeEvent => lifeEvent.id && deleteLifeEvents([lifeEvent.id])}
        />
      </CashflowSection>

      <IncomeModal
        opened={incomeModalOpened}
        onClose={closeIncomeModal}
        onSubmit={handleSaveIncome}
        initialValues={selectedIncome}
      />
      <ExpenditureModal
        opened={expenditureModalOpened}
        onClose={closeExpenditureModal}
        onSubmit={handleSaveExpenditure}
        initialValues={selectedExpenditure}
      />
      <LifeEventModal
        opened={lifeEventModalOpened}
        onClose={closeLifeEventModal}
        onSubmit={handleSaveLifeEvent}
        initialValues={selectedLifeEvent}
      />
    </Stack>
  );
}
